import { Component } from "react";

/**
 * Top-level error boundary. Catches React render-time exceptions so a stray
 * error (e.g. during the Razorpay → verify success path) cannot blank the
 * entire PWA. Renders a minimal recovery screen with a "Reload app" button
 * that re-runs the recharge recovery on next mount.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] React render failure", error, info?.componentStack);
  }

  handleReload = () => {
    try {
      // Soft reload — keeps localStorage (auth token + pending recharge queue),
      // so on remount the recovery flow will pick up any unprocessed payment.
      window.location.assign("/wallet");
    } catch (_e) {
      try { window.location.reload(); } catch (_e2) { /* no-op */ }
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        data-testid="error-boundary"
        className="min-h-screen flex flex-col items-center justify-center p-6 text-center"
      >
        <div className="bg-white rounded-3xl p-8 border-4 border-slate-900/10 shadow-[0_10px_0_rgba(0,0,0,0.08)] max-w-md w-full">
          <div className="text-5xl mb-3">😅</div>
          <h2 className="font-heading text-2xl font-bold text-slate-800">Something glitched.</h2>
          <p className="text-sm text-slate-600 font-semibold mt-2">
            Your wallet & data are safe. If you just made a payment, we'll credit it the moment you reload.
          </p>
          <button
            data-testid="error-boundary-reload"
            onClick={this.handleReload}
            className="mt-5 w-full bg-emerald-500 hover:bg-emerald-600 text-white font-heading text-lg font-bold uppercase tracking-wide py-3 rounded-2xl btn-squish shadow-[0_4px_0_rgba(0,0,0,0.12)]"
          >
            Reload app
          </button>
        </div>
      </div>
    );
  }
}
